import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  Cog6ToothIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  FolderIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../routes/roleOnly";
import { getDisciplines, loadDisciplineToCompany } from "../../../api/hse/disciplines";
import ChecklistTypesStacklist from "../../../components/dashboard/quality/settings/checklist-types-stacklist";

/**
 * HSE/KS Settings - Innstillinger for HMS & KS modulen
 */
export const HseKsSettings = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [activeTab, setActiveTab] = useState("disciplines");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [expandedDisciplines, setExpandedDisciplines] = useState(new Set());

  const canManage = useUserHasAccess({ role: "hse_ks_settings", action: "update" });

  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [companyDisciplines, setCompanyDisciplines] = useState([]);

  // Fetch disciplines on mount
  useEffect(() => {
    fetchDisciplines();
  }, []);

  const fetchDisciplines = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDisciplines();
      const templates = response.templates || [];
      const companyDisciplines = response.company_disciplines || [];
      
      // Create a map of loaded discipline codes for quick lookup
      const loadedCodes = new Set(companyDisciplines.map(d => d.code));
      
      // Mark templates as loaded if they exist in company disciplines
      const templatesWithStatus = templates.map(template => ({
        ...template,
        isLoaded: loadedCodes.has(template.code),
        companyDisciplineId: companyDisciplines.find(d => d.code === template.code)?.id
      }));
      
      setAvailableDisciplines(templatesWithStatus);
      setCompanyDisciplines(companyDisciplines);
    } catch (err) {
      console.error("Error fetching disciplines:", err);
      setError(true);
      toast.error(t("Kunne ikke laste fag/bransjer"));
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if discipline is loaded
  const isDisciplineLoaded = (disciplineCode) => {
    return companyDisciplines.some((d) => d.code === disciplineCode);
  };

  // Old test data - removed, now using API data
  const [oldTestData] = useState([
    {
      id: "prosjektering_vvs",
      name: "Prosjektering VVS",
      description: "Dokumentasjon og prosedyrer for VVS-prosjektering",
      loaded: true,
      loadedAt: "2024-01-15",
      categories: [
        { id: "hms", name: "HMS", chapters: 10 },
        { id: "ks", name: "Kvalitetssystem", chapters: 8 },
        { id: "mil", name: "Miljøstyring", chapters: 6 },
        { id: "lov", name: "Lover og Forskrifter", chapters: 4 },
        { id: "kom", name: "Kompetanse", chapters: 3 },
      ],
    },
    {
      id: "utførende_ventilasjon",
      name: "Utførende ventilasjon",
      description: "Dokumentasjon og prosedyrer for utførende ventilasjonsarbeid",
      loaded: false,
      categories: [
        { id: "hms", name: "HMS", chapters: 12 },
        { id: "ks", name: "Kvalitetssystem", chapters: 9 },
        { id: "mil", name: "Miljøstyring", chapters: 7 },
        { id: "lov", name: "Lover og Forskrifter", chapters: 5 },
        { id: "kom", name: "Kompetanse", chapters: 4 },
      ],
    },
    {
      id: "prosjektering_elektro",
      name: "Prosjektering Elektro",
      description: "Dokumentasjon og prosedyrer for elektro-prosjektering",
      loaded: false,
      categories: [
        { id: "hms", name: "HMS", chapters: 11 },
        { id: "ks", name: "Kvalitetssystem", chapters: 8 },
        { id: "mil", name: "Miljøstyring", chapters: 5 },
        { id: "lov", name: "Lover og Forskrifter", chapters: 4 },
        { id: "kom", name: "Kompetanse", chapters: 3 },
      ],
    },
    {
      id: "utførende_elektro",
      name: "Utførende Elektro",
      description: "Dokumentasjon og prosedyrer for utførende elektroarbeid",
      loaded: false,
      categories: [
        { id: "hms", name: "HMS", chapters: 13 },
        { id: "ks", name: "Kvalitetssystem", chapters: 10 },
        { id: "mil", name: "Miljøstyring", chapters: 6 },
        { id: "lov", name: "Lover og Forskrifter", chapters: 5 },
        { id: "kom", name: "Kompetanse", chapters: 4 },
      ],
    },
  ]);

  const tabs = [
    { id: "disciplines", label: t("Fag/Bransje"), icon: BuildingOfficeIcon },
    { id: "checklist_types", label: t("Sjekklistetyper"), icon: ClipboardDocumentListIcon },
    // Flere tabs kan legges til senere:
    // { id: "categories", label: t("Kategorier"), icon: FolderIcon },
    // { id: "chapters", label: t("Kapitler"), icon: DocumentTextIcon },
  ];

  const toggleDiscipline = (disciplineId) => {
    const newExpanded = new Set(expandedDisciplines);
    if (newExpanded.has(disciplineId)) {
      newExpanded.delete(disciplineId);
    } else {
      newExpanded.add(disciplineId);
    }
    setExpandedDisciplines(newExpanded);
  };

  const handleLoadDiscipline = async (disciplineId, isUpdate = false) => {
    try {
      setLoading(true);
      const response = await loadDisciplineToCompany(disciplineId);
      
      if (isUpdate) {
        toast.success(t("Fag/bransje oppdatert"));
      } else {
        toast.success(t("Fag/bransje lastet inn"));
      }
      
      // Refresh disciplines list
      await fetchDisciplines();
    } catch (err) {
      console.error("Error loading/updating discipline:", err);
      const errorMessage = err.response?.data?.errors?.[0] || 
        (isUpdate ? t("Kunne ikke oppdatere fag/bransje") : t("Kunne ikke laste inn fag/bransje"));
      toast.error(errorMessage);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Note: Unloading disciplines is not implemented as per requirements
  // Users must manually delete chapters/categories if needed

  const renderDisciplineStructure = (discipline) => {
    const isExpanded = expandedDisciplines.has(discipline.id || discipline.code);
    const loaded = discipline.isLoaded || isDisciplineLoaded(discipline.code);
    const totalChapters = discipline.categories?.reduce((sum, cat) => {
      return sum + (cat.chapters?.length || 0);
    }, 0) || 0;

    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <button
                onClick={() => toggleDiscipline(discipline.id || discipline.code)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>
              <BuildingOfficeIcon className="h-6 w-6 text-blue-500" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {discipline.name}
                </h3>
                {discipline.description && (
                  <p className="text-sm text-gray-500 mt-1">
                    {discipline.description}
                  </p>
                )}
              </div>
            </div>

            {isExpanded && discipline.categories && (
              <div className="ml-10 mt-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {discipline.categories.map((category) => (
                    <div
                      key={category.id || category.code}
                      className="flex items-center space-x-2 p-2 bg-gray-50 rounded border border-gray-200"
                    >
                      <FolderIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {category.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {category.chapters?.length || 0} {t("kapitler")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <strong>{t("Totalt")}:</strong> {discipline.categories.length} {t("kategorier")}, {totalChapters} {t("kapitler")}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="ml-4 flex items-center space-x-2">
            {loaded ? (
              <>
                <div className="flex items-center space-x-1 text-green-600">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">{t("Lastet inn")}</span>
                </div>
                {canManage && (
                  <button
                    onClick={() => handleLoadDiscipline(discipline.id || discipline.code, true)}
                    disabled={loading}
                    className="ml-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                    title={t("Oppdater fra template")}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>{t("Oppdater")}</span>
                  </button>
                )}
              </>
            ) : (
              canManage && (
                <button
                  onClick={() => handleLoadDiscipline(discipline.id || discipline.code, false)}
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>{t("Last inn")}</span>
                </button>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardHeader
      title={t("Innstillinger")}
      subtitle={t("Administrer innstillinger for HMS & KS modulen")}
    >
      <div className="mt-8 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activeTab === tab.id
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === "disciplines" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t("Fag/Bransje")}
                </h3>
                <p className="text-sm text-blue-700">
                  {t("Velg og last inn fag/bransjer for å aktivere dokumentstrukturen. Hver fag/bransje inneholder kategorier og kapitler som kan brukes i dokumenthåndteringen.")}
                </p>
              </div>

              {loading ? (
                <ResourceLoading size="base" />
              ) : error ? (
                <ResourceError errors={[t("Kunne ikke laste fag/bransjer")]} />
              ) : availableDisciplines.length === 0 ? (
                <ResourceEmpty message={t("Ingen fag/bransjer tilgjengelig")} />
              ) : (
                <div className="space-y-4">
                  {availableDisciplines.map((discipline) => (
                    <div key={discipline.id || discipline.code}>
                      {renderDisciplineStructure(discipline)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "checklist_types" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t("Sjekklistetyper")}
                </h3>
                <p className="text-sm text-blue-700">
                  {t("Administrer sjekklistetyper som kan brukes i HMS & KS modulen.")}
                </p>
              </div>
              <ChecklistTypesStacklist />
            </div>
          )}
        </div>
      </div>
    </DashboardHeader>
  );
};
